import { useState, useCallback, useEffect } from 'react';
import InputSection from './InputSection';
import PaymentRoutesTable from './PaymentRoutesTable';
import { toast } from 'sonner';
import { routeAPI } from '../services/routeAPI';
import { useFileHandlers } from '../hooks/useFileHandlers';
import { modifySwiftXML } from '../utils/xmlModifier';

const RouteVisualization = () => {
  const [payments, setPayments] = useState([]);
  const [originalXmlContents, setOriginalXmlContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [error, setError] = useState(null);
  const [routesInDb, setRoutesInDb] = useState(0);
  const [uniqueCBs, setUniqueCBs] = useState(0);

  const fetchRoutesCount = useCallback(async () => {
    try {
      const data = await routeAPI.getRoutesCount();
      setRoutesInDb(data.count);
      setUniqueCBs(data.unique_cbs || 0);
    } catch (err) {
      console.error('Failed to fetch routes count:', err);
    }
  }, []);

  useEffect(() => {
    fetchRoutesCount();
  }, [fetchRoutesCount]);

  const handleFindBestRoute = useCallback(async (paymentsToProcess) => {
    const targetPayments = paymentsToProcess || payments;
    
    if (targetPayments.length === 0) {
      toast.error('Please upload SWIFT XML first');
      return;
    }

    if (routesInDb === 0) {
      toast.error('Please upload CB routes database first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updatedPayments = await Promise.all(
        targetPayments.map(async ({ payment }) => {
          try {
            const data = await routeAPI.findOptimalRoute(
              payment.Corridor,
              payment.Currency,
              payment.Amount || 1000000
            );
            
            return {
              payment,
              optimalRoute: data.best_route,
              alternatesSent: data.alternates_sent || 0
            };
          } catch (err) {
            console.error('Failed to find optimal route:', err);
            return {
              payment,
              optimalRoute: null,
              alternatesSent: 0
            };
          }
        })
      );
      
      setPayments(updatedPayments);
      
      const successCount = updatedPayments.filter(p => p.optimalRoute).length;
      if (successCount > 0) {
        toast.success(`✓ Found optimal routes for ${successCount} of ${targetPayments.length} payment(s)!`);
      } else {
        throw new Error('No optimal routes found');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to find optimal routes';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [payments, routesInDb]);

  const { handleXmlUpload } = useFileHandlers(
    setPayments,
    setOriginalXmlContents,
    setError,
    routesInDb,
    handleFindBestRoute
  );

  const handleDownload = useCallback((paymentIndex) => {
    if (paymentIndex >= payments.length) return;

    const payment = payments[paymentIndex];
    if (!payment.optimalRoute) return;

    try {
      // Use the specific message's XML, not the entire file
      const sourceXml = payment.payment._sourceXml;
      if (!sourceXml) {
        toast.error('Source XML not available for this payment');
        return;
      }
      
      const modifiedXml = modifySwiftXML(sourceXml, payment.optimalRoute);
      
      const blob = new Blob([modifiedXml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment_${paymentIndex + 1}_optimal_${payment.optimalRoute.cb_bic}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`✓ Downloaded message ${paymentIndex + 1} with optimal route`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download modified XML: ' + err.message);
    }
  }, [payments]);

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingCsv(true);
    setError(null);

    try {
      const data = await routeAPI.uploadCSV(file);
      
      if (data.success) {
        setRoutesInDb(data.count);
        setUniqueCBs(data.unique_cbs || 0);
        toast.success(`✓ ${data.message} - Database updated`);
        
        if (payments.length > 0) {
          toast.info('Auto-finding optimal routes...');
          setTimeout(() => handleFindBestRoute(payments), 500);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to upload CSV';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploadingCsv(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <InputSection
          onXmlUpload={handleXmlUpload}
          onCsvUpload={handleCsvUpload}
          loading={loading}
          uploadingCsv={uploadingCsv}
          error={error}
          routesInDb={routesInDb}
          uniqueCBs={uniqueCBs}
        />
        <PaymentRoutesTable 
          payments={payments}
          onDownload={handleDownload}
        />
      </div>
    </div>
  );
};

export default RouteVisualization;
